import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages the cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      CartAddEvent.eventName,
      this.#handleCartAdd
    );
  }

  #refreshDrawer = async () => {
    const currentComponent =
      this.querySelector(
        'cart-items-component'
      );

    if (!currentComponent) return;

    const sectionId =
      currentComponent.dataset.sectionId;

    if (!sectionId) return;

    const url =
      new URL(window.location.href);

    url.searchParams.set(
      'section_id',
      sectionId
    );

    const response =
      await fetch(
        url.toString(),
        {
          headers: {
            'X-Requested-With':
              'XMLHttpRequest'
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        'Unable to refresh cart drawer.'
      );
    }

    const html =
      await response.text();

    const parser =
      new DOMParser();

    const newDocument =
      parser.parseFromString(
        html,
        'text/html'
      );

    const newComponent =
      newDocument.querySelector(
        'cart-items-component'
      );

    if (!newComponent) {
      return;
    }

    currentComponent.innerHTML =
      newComponent.innerHTML;

    const currentDialog =
      this.querySelector(
        '.aim-cart__dialog'
      );

    const newDialog =
      newDocument.querySelector(
        '.aim-cart__dialog'
      );

    if (
      currentDialog &&
      newDialog
    ) {
      currentDialog.classList.toggle(
        'cart-drawer--empty',
        newDialog.classList.contains(
          'cart-drawer--empty'
        )
      );
    }

    const currentTrigger =
      this.querySelector(
        '[data-testid="cart-drawer-trigger"]'
      );

    const newTrigger =
      newDocument.querySelector(
        '[data-testid="cart-drawer-trigger"]'
      );

    if (
      currentTrigger &&
      newTrigger
    ) {
      currentTrigger.innerHTML =
        newTrigger.innerHTML;
    }
  };

  #handleCartAdd = async () => {
    try {
      await this.#refreshDrawer();
    } catch (error) {
      console.error(
        'Cart drawer refresh failed:',
        error
      );
    }

    if (
      this.hasAttribute(
        'auto-open'
      )
    ) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    customElements
      .whenDefined(
        'shopify-payment-terms'
      )
      .then(() => {
        const installmentsContent =
          document.querySelector(
            'shopify-payment-terms'
          )?.shadowRoot;

        const cta =
          installmentsContent
            ?.querySelector(
              '#shopify-installments-cta'
            );

        cta?.addEventListener(
          'click',
          this.closeDialog,
          {
            once: true
          }
        );
      });
  }

  close() {
    this.closeDialog();
  }
}

if (
  !customElements.get(
    'cart-drawer-component'
  )
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
